import { useEffect, useState } from "react";

/**
 * Reports whether the returned ref's element has entered the viewport, latching
 * on the first intersection. Reports `true` with no `IntersectionObserver`.
 *
 * @param {{ threshold?: number, rootMargin?: string }} options
 * @returns {[(element: Element | null) => void, boolean]} Callback ref — rather
 *   than an object one, so an element mounting later is still observed.
 */
export const useInViewOnce = ({ threshold = 0, rootMargin = "0px" } = {}) => {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (inView || !element) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      { threshold, rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, inView, threshold, rootMargin]);

  return [setElement, inView];
};

/**
 * Reports whether the returned ref's element is currently in the viewport,
 * toggling back to `false` when it scrolls out. Use to pause offscreen work
 * (looping animations, timers) so it stops consuming compositing memory.
 * Reports `true` with no `IntersectionObserver`.
 *
 * @param {{ threshold?: number, rootMargin?: string }} options
 * @returns {[(element: Element | null) => void, boolean]} Callback ref + live in-view flag.
 */
export const useInView = ({ threshold = 0, rootMargin = "0px" } = {}) => {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (!element || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, threshold, rootMargin]);

  return [setElement, inView];
};
